import React from 'react'
import PropTypes from 'prop-types'
import { useSetState } from 'react-use'
import moment from 'moment'
import queryString from 'query-string'
import { flowRight, isEmpty, isArray, isEqual } from 'lodash'
import { withTranslationCustom } from 'lib/i18next'
import { withRouter } from 'react-router-dom'
import { withErrorBoundaryNoRender } from 'lib/errorBoundary'
import { EVENT_TYPE } from 'lib/utils/store/event'

import AppContext from 'components/AppContext'
import ExtendedDateRangePicker from 'components/Forms/Date/ExtendedDateRangePicker/ExtendedDateRangePicker'
import FormElementGroup from 'components/FormElements/FormElementGroup'
import FormElement from 'components/Forms/FormElements/FormElement'
import Button from 'components/UI/Button'

import './timeline-filters.pcss'

export const defaultFilters = Object.freeze({
    types: [],
    eventTimeStart: undefined,
    eventTimeEnd: undefined,
})

export const filtersToObject = (search) => {
    const query = queryString.parse(search)

    const { fstart, fend } = query

    const filters = {}

    if (query['ftype[]']) {
        const types = isArray(query['ftype[]']) ? query['ftype[]'] : [query['ftype[]']]
        filters.types = types
            .map((type) => type.toUpperCase())
            .filter((type) => Object.values(EVENT_TYPE).includes(type))
    }

    if (fstart && moment(fstart, 'YYYY-MM-DD').isValid()) {
        filters.eventTimeStart = fstart
    }

    if (fend && moment(fend, 'YYYY-MM-DD').isValid()) {
        filters.eventTimeEnd = fend
    }

    return isEmpty(filters) ? null : filters
}

export const filtersToString = (object) => {
    return queryString.stringify({
        ftype: object.types,
        fstart: object.eventTimeStart,
        fend: object.eventTimeEnd,
    }, {
        arrayFormat: 'bracket',
    }).toLowerCase()
}

const TimelineFilters = ({ t, filters, onChange }) => {
    const [state, setState] = useSetState({
        focusedDate: null,
    })

    const { focusedDate } = state

    const handleTypesSelectAll = (e) => {
        if (e.target.value) {
            onChange({
                ...filters,
                types: [],
            })
        }
    }

    const handleReset = () => {
        onChange({ ...defaultFilters })
    }

    const handleTypesChange = (e, type) => {
        if (filters.types.includes(type)) {
            onChange({
                ...filters,
                types: filters.types.filter(entry => entry !== type)
            })
        } else {
            onChange({
                ...filters,
                types: filters.types ? [...filters.types, type] : [type]
            })
        }
    }

    const handleDateFilterReset = () => {
        onChange({
            ...filters,
            eventTimeStart: undefined,
            eventTimeEnd: undefined,
        })
    }

    const handleDateChange = ([start, end]) => {
        onChange({
            ...filters,
            eventTimeStart: start ? moment(start).format('YYYY-MM-DD') : null,
            eventTimeEnd: end ? moment(end).format('YYYY-MM-DD') : null,
        })
    }

    const dirty = !isEqual(filters, defaultFilters)

    const dateRangeFilterActive = filters.eventTimeStart || filters.eventTimeEnd

    return (
        <AppContext context="timelineFilters">
            <div className="timeline-filters">
                <div className="timeline-filters__header">
                    <h1>{t('events:timeline.filters.modalTitle')}</h1>
                    {dirty && (
                        <Button
                            onClick={handleReset}
                            trackName="reset"
                            className="button button--plain"
                        >
                            {t('events:timeline.filters.reset')}
                        </Button>
                    )}
                </div>
                <FormElementGroup
                    title="Aikarajaus"
                    cssClass="timeline-filters__filter-date"
                >
                    <FormElement
                        name="dateRange"
                        elementClass="form-element--date-time-range-input"
                        value={true}
                    >
                        {() => (
                            <ExtendedDateRangePicker
                                onDateChange={handleDateChange}
                                onFocusChange={(focused) => {
                                    setState({ focusedDate: focused })
                                }}
                                focusedInput={focusedDate}
                                startDate={filters.eventTimeStart ? moment(filters.eventTimeStart).toDate() : null}
                                startDateId="startDate"
                                endDate={filters.eventTimeEnd ? moment(filters.eventTimeEnd).toDate() : null}
                                endDateId="endDate"
                                disableFutureDates={true}
                                placeholderText={t('events:timeline.filters.dateRangeFilter.inputPlaceholder')}
                            />
                        )}
                    </FormElement>
                    {dateRangeFilterActive && (
                        <Button
                            onClick={handleDateFilterReset}
                            trackName="removeFilterDate"
                            className="button button--plain"
                        >
                            {t('events:timeline.filters.dateRangeFilter.reset')}
                        </Button>
                    )}
                </FormElementGroup>
                <FormElementGroup
                    title={t('events:timeline.filters.eventTypeFilter.title')}
                    cssClass="timeline-filters__filter-type form-element--option-group"
                >
                    <input
                        type="radio"
                        id="all"
                        name="all"
                        value="all"
                        onChange={e => handleTypesSelectAll(e)}
                        checked={filters.types.length === 0}
                    />
                    <label htmlFor="all">Kaikki tapahtumat</label>
                    <div className="timeline-filters__filter-type-options">
                        {Object.values(EVENT_TYPE).map((type) => (
                            <React.Fragment key={type}>
                                <input
                                    type="checkbox"
                                    id={type}
                                    value={type}
                                    checked={filters.types.includes(type)}
                                    onChange={e => handleTypesChange(e, type)}
                                />
                                <label htmlFor={type}>{t(`events:${type}.title`)}</label>
                            </React.Fragment>
                        ))}
                    </div>
                </FormElementGroup>
            </div>
        </AppContext>
    )
}

TimelineFilters.propTypes = {
    filters: PropTypes.object,
    onChange: PropTypes.func.isRequired,
}

export default flowRight(
    withErrorBoundaryNoRender,
    withRouter,
    withTranslationCustom('events'),
)(TimelineFilters)
